import { useLayoutEffect, useState } from "react";
import { ProgramPhase } from "@platform/types/platform.types";
import { ProgramPhaseItemContainer } from "./ProgramPhaseItemContainer";
import { ProgramPhaseListNavigation } from "./ProgramPhaseListNavigation";

type Props = {
  currentPhase: ProgramPhase;
  phases: ProgramPhase[];
  selectedPhase: ProgramPhase | undefined;
  setSelectedPhase: (phase: ProgramPhase) => void;
  phaseContainerRef: React.RefObject<HTMLDivElement>;
  onPhaseListReady: () => void;
  isLoading: boolean;
};

export function ProgramPhaseList({
  currentPhase,
  phases,
  selectedPhase,
  setSelectedPhase,
  phaseContainerRef,
  onPhaseListReady,
  isLoading,
}: Props) {
  const [phaseContainerHeight, setPhaseContainerHeight] = useState<
    number | null
  >(null);

  useLayoutEffect(() => {
    const container = phaseContainerRef.current;

    if (!container || container.children.length !== phases.length) return;
    if (selectedPhase) return;

    requestAnimationFrame(() => {
      onPhaseListReady();
    });
  }, [phases.length, onPhaseListReady]);

  // Set container height to the height of the current phase
  useLayoutEffect(() => {
    if (!phaseContainerRef.current) return;

    const container = phaseContainerRef.current;

    const currentPhaseElementIndex = Array.from(container.children).findIndex(
      (el, i) => phases[i].id === selectedPhase?.id
    );

    const currentPhaseElement = Array.from(container.children)[
      currentPhaseElementIndex
    ];

    const currentPhaseObject = phases[currentPhaseElementIndex];

    if (!currentPhaseElement) return;

    if (!currentPhaseObject.actions || currentPhaseObject.actions.length === 0)
      return;

    const containerHeight = currentPhaseElement.getBoundingClientRect().height;

    setPhaseContainerHeight(containerHeight + 96);
  }, [selectedPhase, phases]);

  return (
    <>
      <div
        ref={phaseContainerRef}
        className="flex flex-row overflow-x-auto gap-1 items-start transition-all duration-300 ease-in-out overflow-y-hidden scrollbar-hide w-full"
        style={
          phaseContainerHeight
            ? { maxHeight: `${phaseContainerHeight}px`, overflowX: "hidden" }
            : { overflowX: "hidden" }
        }
      >
        {phases.map((phase) => (
          <ProgramPhaseItemContainer
            key={phase.id}
            data-id={phase.id}
            phase={phase}
            currentPhase={currentPhase}
            isLoading={isLoading}
          />
        ))}
      </div>
      <ProgramPhaseListNavigation
        phases={phases}
        selectedPhase={selectedPhase}
        setSelectedPhase={setSelectedPhase}
      />
    </>
  );
}

export default ProgramPhaseList;
